import { useState } from 'react';
import { useParams } from 'react-router';
import { Download, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MarkdownContent } from '@/components/markdown-content';
import { usePersonaById, usePersonaMarkdown } from '@/hooks/use-personas';
import { useAccountUsers } from '@/hooks/use-account-users';
import { usePermissions } from '@/hooks/use-permissions';
import { PersonaGuideEditor } from '@/modules/persona/components/persona-guide-editor';

// Standalone (no app chrome) so the new tab it opens in is just the guide, ready to
// print/"Save as PDF" — the sidebar/nav would otherwise show up in the printout.
export default function PersonaGuideRoute() {
	const { id } = useParams<{ id: string }>();
	const { data: persona, isLoading: isPersonaLoading } = usePersonaById(id ?? '');
	const { data: markdown, isLoading: isMarkdownLoading } = usePersonaMarkdown(id ?? '');
	const { data: accountUsers } = useAccountUsers();
	const { can } = usePermissions();
	// The version being edited, fixed when Edit is clicked so a refetch cannot change it mid-edit.
	const [editedVersion, setEditedVersion] = useState<string | null>(null);
	const isEditing = editedVersion !== null;

	const name =
		persona?.userId == null ? 'My Brand' : accountUsers?.find((u) => u.id === persona.userId)?.name ?? 'Agent';

	const isLoading = isPersonaLoading || isMarkdownLoading;
	// A regenerate in progress would replace the edit, so editing waits for it to finish.
	const canEdit = can('edit', 'persona') && !!persona && !!markdown && persona.generationStatus !== 'generating';

	return (
		<div className='min-h-screen bg-muted/30'>
			<div className='print:hidden sticky top-0 z-10 flex items-center justify-between border-b bg-background px-6 py-3'>
				<h1 className='text-sm font-semibold'>{persona ? `${name} — Persona Guide` : 'Persona Guide'}</h1>
				{!isEditing && (
					<div className='flex items-center gap-2'>
						{canEdit && (
							<Button variant='outline' className='rounded-full gap-1.5' onClick={() => persona && setEditedVersion(persona.currentVersion)}>
								<Pencil className='size-3.5' />
								Edit
							</Button>
						)}
						<Button className='rounded-full gap-1.5' onClick={() => window.print()} disabled={!markdown}>
							<Download className='size-3.5' />
							Download PDF
						</Button>
					</div>
				)}
			</div>

			<div className='mx-auto max-w-3xl px-6 py-10 print:px-0 print:py-0'>
				<div className='rounded-2xl border bg-white p-10 shadow-sm print:rounded-none print:border-0 print:shadow-none print:p-0'>
					{isLoading ? (
						<div className='flex flex-col gap-2'>
							<Skeleton className='h-5 w-48' />
							<Skeleton className='h-3 w-full' />
							<Skeleton className='h-3 w-full' />
							<Skeleton className='h-3 w-3/4' />
						</div>
					) : editedVersion !== null && persona ? (
						<PersonaGuideEditor
							personaId={persona.id}
							baseVersion={editedVersion}
							onDone={() => setEditedVersion(null)}
						/>
					) : markdown ? (
						<MarkdownContent text={markdown} className='text-sm text-foreground' />
					) : (
						<p className='text-sm text-muted-foreground'>No persona guide generated yet.</p>
					)}
				</div>
			</div>
		</div>
	);
}
